import { Bookmark, BookmarkCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './FlashcardBlock.css';
import { ICON_SIZE } from '../design-system';

/**
 * FlashcardBlock - Inline flashcard preview component for AI chat responses
 * Displays front (question) and back (answer) in a compact card format
 * with individual save-to-deck functionality.
 */
const FlashcardBlock = ({
  front,
  back,
  onSave,
  isSaved = false,
  disabled = false,
}) => {
  const { t } = useTranslation('cards');

  // Don't render if no content
  if (!front && !back) {
    return null;
  }

  const handleSave = (e) => {
    e?.stopPropagation();
    if (onSave && !disabled) {
      onSave();
    }
  };

  return (
    <div className={`flashcard-block ${isSaved ? 'saved' : ''}`}>
      {/* Front (Question) */}
      <div className="flashcard-block-front">
        <span className="flashcard-label">{t('flashcardBlock.questionLabel')}</span>
        <div className="flashcard-content">
          {front || t('flashcardBlock.noQuestion')}
        </div>
      </div>

      {/* Divider */}
      <div className="flashcard-block-divider" />

      {/* Back (Answer) */}
      <div className="flashcard-block-back">
        <span className="flashcard-label">{t('flashcardBlock.answerLabel')}</span>
        <div className="flashcard-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {back || t('flashcardBlock.noAnswer')}
          </ReactMarkdown>
        </div>
      </div>

      {/* Save button */}
      {!disabled && (
        <button
          className={`flashcard-save-btn ${isSaved ? 'saved' : ''}`}
          onClick={handleSave}
          title={isSaved ? t('flashcardBlock.alreadySaved') : t('flashcardBlock.saveToDeck')}
        >
          {isSaved ? (
            <>
              <BookmarkCheck size={ICON_SIZE.xs} />
              <span>{t('flashcardBlock.saved')}</span>
            </>
          ) : (
            <>
              <Bookmark size={ICON_SIZE.xs} />
              <span>{t('flashcardBlock.save')}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default FlashcardBlock;
